import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Buffer } from 'buffer';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import type { CloudApiClient, CloudBackup } from './api-client';
import { CloudApiError } from './api-client';
import { CLOUD_NAMESPACE } from './cloud-i18n';
import { SnapshotTransferError } from './snapshot-transfer';
import { useCloudBackups } from './useCloudBackups';

type Action = Readonly<{
  backupId: string | null;
  type: 'create' | 'download' | 'restore';
}> | null;

export function CloudBackupsCard({
  api,
  subject,
  workspaceId,
  localBudgetId,
  localBudgetName,
  isOffline,
  isEndToEndEncrypted,
  showBudgetBinding = true,
}: Readonly<{
  api: CloudApiClient;
  subject: string;
  workspaceId: string | null;
  localBudgetId: string | null;
  localBudgetName: string | null;
  isOffline: boolean;
  isEndToEndEncrypted: boolean;
  showBudgetBinding?: boolean;
}>) {
  const { i18n, t } = useTranslation(CLOUD_NAMESPACE);
  const backups = useCloudBackups(
    api,
    subject,
    workspaceId,
    localBudgetId,
    localBudgetName,
    isOffline,
    isEndToEndEncrypted,
  );
  const [action, setAction] = useState<Action>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const activeBudgets = backups.budgets.filter(
    budget => budget.status === 'active',
  );
  const sortedBackups = [...backups.backups].sort((left, right) =>
    right.created_at.localeCompare(left.created_at),
  );
  const isUnavailable =
    workspaceId === null || localBudgetId === null || isOffline;

  async function createBackup() {
    setAction({ backupId: null, type: 'create' });
    clearMessages();
    try {
      await backups.createBackup();
      setSuccess(t('Backup created and verified.'));
    } catch (error) {
      setActionError(toError(error));
    } finally {
      setAction(null);
    }
  }

  async function downloadBackup(backup: CloudBackup) {
    setAction({ backupId: backup.id, type: 'download' });
    clearMessages();
    try {
      const bytes = await backups.downloadBackup(backup);
      await window.Actual.saveFile(
        Buffer.from(bytes),
        `budget-backup-${backup.created_at.slice(0, 10)}.zip`,
        t('Download'),
      );
      setSuccess(t('Backup downloaded.'));
    } catch (error) {
      setActionError(toError(error));
    } finally {
      setAction(null);
    }
  }

  async function restoreBackup(backup: CloudBackup) {
    const formattedDate = formatDate(backup.created_at, i18n.resolvedLanguage);
    if (
      !window.confirm(
        t(
          'Restore {{date}} as a new local copy? Your current budget will not be replaced.',
          { date: formattedDate },
        ),
      )
    ) {
      return;
    }
    setAction({ backupId: backup.id, type: 'restore' });
    clearMessages();
    try {
      await backups.restoreBackupAsCopy(
        backup,
        t('{{name}} (restored copy)', {
          name: localBudgetName ?? 'Budget',
        }),
      );
      setSuccess(t('Restored as a new local copy.'));
    } catch (error) {
      setActionError(toError(error));
    } finally {
      setAction(null);
    }
  }

  function clearMessages() {
    setActionError(null);
    setSuccess(null);
  }

  const error = actionError ?? backups.error;
  return (
    <Card style={{ margin: 0 }}>
      <section aria-labelledby="cloud-backups-heading">
        <View style={{ padding: 18, gap: 14 }}>
          <View
            style={{
              alignItems: 'center',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 10,
              justifyContent: 'space-between',
            }}
          >
            <h2 id="cloud-backups-heading" style={{ fontSize: 18, margin: 0 }}>
              <Trans ns={CLOUD_NAMESPACE}>Backups</Trans>
            </h2>
            <ButtonWithLoading
              variant="primary"
              isLoading={action?.type === 'create'}
              isDisabled={
                isUnavailable ||
                isEndToEndEncrypted ||
                action !== null ||
                backups.status === 'loading' ||
                (backups.selectedBudgetId === null && activeBudgets.length > 0)
              }
              onPress={() => void createBackup()}
            >
              {action?.type === 'create' ? (
                <Trans ns={CLOUD_NAMESPACE}>Creating backup...</Trans>
              ) : (
                <Trans ns={CLOUD_NAMESPACE}>Create backup</Trans>
              )}
            </ButtonWithLoading>
          </View>

          {localBudgetId === null && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>
                Open a local budget to manage its backups.
              </Trans>
            </p>
          )}
          {localBudgetId !== null && workspaceId === null && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>Select workspace</Trans>
            </p>
          )}
          {isOffline && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>
                Backups require an Internet connection. Your local budget
                remains available.
              </Trans>
            </p>
          )}
          {isEndToEndEncrypted && (
            <p role="alert" style={errorStyle}>
              <Trans ns={CLOUD_NAMESPACE}>
                Cloud backup is unavailable for encrypted budgets until
                client-side snapshot encryption is supported.
              </Trans>
            </p>
          )}

          {showBudgetBinding &&
            !isUnavailable &&
            activeBudgets.length > 1 && (
              <label style={{ display: 'grid', gap: 6, maxWidth: 440 }}>
                <span style={{ fontWeight: 600 }}>
                  <Trans ns={CLOUD_NAMESPACE}>Select a cloud budget</Trans>
                </span>
                <select
                  aria-label={t('Select a cloud budget')}
                  value={backups.selectedBudgetId ?? ''}
                  disabled={action !== null}
                  onChange={event =>
                    void backups.selectBudget(event.currentTarget.value)
                  }
                  style={inputStyle}
                >
                  <option value="" disabled>
                    <Trans ns={CLOUD_NAMESPACE}>Select a cloud budget</Trans>
                  </option>
                  {activeBudgets.map(budget => (
                    <option key={budget.id} value={budget.id}>
                      {budget.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

          {showBudgetBinding &&
            !isUnavailable &&
            activeBudgets.length === 0 && (
              <p style={subduedStyle}>
                <Trans ns={CLOUD_NAMESPACE}>
                  The current local budget will be registered in the active
                  workspace when its first backup is created.
                </Trans>
              </p>
            )}

          {backups.status === 'loading' && (
            <output style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>Loading backups...</Trans>
            </output>
          )}
          {error !== null && (
            <View style={{ gap: 8 }}>
              <p role="alert" style={errorStyle}>
                {backupErrorMessage(error, t)}
              </p>
              {backups.status === 'error' && (
                <Button onPress={() => void backups.reload()}>
                  <Trans ns={CLOUD_NAMESPACE}>Retry</Trans>
                </Button>
              )}
            </View>
          )}
          {success !== null && <output style={successStyle}>{success}</output>}

          {backups.status === 'ready' && sortedBackups.length === 0 && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>No backups yet</Trans>
            </p>
          )}
          {sortedBackups.length > 0 && (
            <ul style={listStyle}>
              {sortedBackups.map(backup => {
                const isVerified = backups.verifiedBackupIds.has(backup.id);
                return (
                  <li key={backup.id} style={backupRowStyle}>
                    <View style={{ gap: 4 }}>
                      <time
                        dateTime={backup.created_at}
                        style={{ fontWeight: 600 }}
                      >
                        {formatDate(backup.created_at, i18n.resolvedLanguage)}
                      </time>
                      <span style={subduedStyle}>
                        {t('{{size}} bytes', {
                          size: new Intl.NumberFormat(
                            i18n.resolvedLanguage,
                          ).format(backup.size_bytes),
                        })}
                      </span>
                      <span style={subduedStyle}>
                        {isVerified ? t('Verified') : t('Cataloged')}
                      </span>
                    </View>
                    <View
                      style={{
                        alignItems: 'center',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <ButtonWithLoading
                        isLoading={
                          action?.type === 'download' &&
                          action.backupId === backup.id
                        }
                        isDisabled={action !== null || isOffline}
                        onPress={() => void downloadBackup(backup)}
                      >
                        {action?.type === 'download' &&
                        action.backupId === backup.id ? (
                          <Trans ns={CLOUD_NAMESPACE}>Downloading...</Trans>
                        ) : (
                          <Trans ns={CLOUD_NAMESPACE}>Download</Trans>
                        )}
                      </ButtonWithLoading>
                      <ButtonWithLoading
                        isLoading={
                          action?.type === 'restore' &&
                          action.backupId === backup.id
                        }
                        isDisabled={action !== null || isOffline}
                        onPress={() => void restoreBackup(backup)}
                      >
                        {action?.type === 'restore' &&
                        action.backupId === backup.id ? (
                          <Trans ns={CLOUD_NAMESPACE}>Restoring...</Trans>
                        ) : (
                          <Trans ns={CLOUD_NAMESPACE}>Restore as copy</Trans>
                        )}
                      </ButtonWithLoading>
                    </View>
                  </li>
                );
              })}
            </ul>
          )}
          {sortedBackups.length > 0 && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>
                Backup status is verified when it is downloaded.
              </Trans>
            </p>
          )}
        </View>
      </section>
    </Card>
  );
}

function backupErrorMessage(error: Error, t: (key: string) => string): string {
  if (
    error instanceof SnapshotTransferError &&
    error.code === 'integrity-failed'
  ) {
    return t('Backup integrity verification failed.');
  }
  if (error instanceof CloudApiError) {
    if (error.kind === 'forbidden') {
      return t('Permission denied for this backup.');
    }
    if (error.backendCode === 'backup-not-ready') {
      return t('Upload is incomplete. Try again later or create a new backup.');
    }
    if (error.backendCode === 'backup-integrity-failed') {
      return t('Backup integrity verification failed.');
    }
  }
  if (error.message === 'Select the cloud budget before creating a backup.') {
    return t('Select a cloud budget before creating a backup.');
  }
  return t('Could not complete the backup operation.');
}

function formatDate(value: string, locale?: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error('Unexpected backup error.');
}

const subduedStyle = {
  color: theme.pageTextSubdued,
  lineHeight: 1.45,
  margin: 0,
} as const;

const errorStyle = {
  color: theme.errorText,
  lineHeight: 1.45,
  margin: 0,
} as const;

const successStyle = {
  color: theme.noticeText,
  lineHeight: 1.45,
} as const;

const inputStyle = {
  background: theme.tableBackground,
  border: `1px solid ${theme.formInputBorder}`,
  borderRadius: 5,
  color: theme.formInputText,
  font: 'inherit',
  minHeight: 42,
  padding: '8px 10px',
  width: '100%',
} as const;

const listStyle = {
  display: 'grid',
  gap: 8,
  listStyle: 'none',
  margin: 0,
  padding: 0,
} as const;

const backupRowStyle = {
  alignItems: 'center',
  background: theme.pageBackground,
  borderRadius: 6,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  justifyContent: 'space-between',
  minHeight: 62,
  padding: 10,
} as const;
